import { useEffect } from "react";
import { createAuthClient } from "better-auth/react";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE } from "./api";

export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
};

// Better-Auth React client. Cross-origin requests need credentials: "include"
// so the session cookie rides along.
export const authClient = createAuthClient({
  baseURL: API_BASE,
  fetchOptions: {
    credentials: "include",
  },
});

// ── Optimistic auth (#2) ────────────────────────────────────────────────────
// Cache the last-known user (id/email/name only, NOT a token; the httpOnly
// session cookie stays the real credential) so a returning user renders
// signed-in the instant the app opens, while better-auth validates in the
// background. This is what makes big apps feel "always signed in".
const CACHED_USER_KEY = "assembly.auth.user";

function readCachedUser(): AuthUser | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHED_USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(user: AuthUser | null) {
  if (typeof window === "undefined") return;
  try {
    if (user) window.localStorage.setItem(CACHED_USER_KEY, JSON.stringify(user));
    else window.localStorage.removeItem(CACHED_USER_KEY);
  } catch {
    /* storage disabled, optimistic render just won't kick in */
  }
}

// Module-scoped snapshot so the very FIRST render (before any effect runs) can
// paint optimistically with no flash. Kept in sync as the real session settles.
let cachedUserSnapshot: AuthUser | null = readCachedUser();

// True if we have a last-known user to render optimistically on cold launch.
export function hasCachedUser(): boolean {
  return cachedUserSnapshot !== null;
}

export function useAuth() {
  const session = authClient.useSession();
  const resolvedUser = (session.data?.user ?? null) as AuthUser | null;

  // Persist / clear the cache once the real session settles, so the NEXT cold
  // launch can render instantly. A genuine expiry (resolves null) clears it.
  useEffect(() => {
    if (session.isPending) return;
    cachedUserSnapshot = resolvedUser;
    writeCachedUser(resolvedUser);
  }, [session.isPending, resolvedUser]);

  // While the first check is in flight, fall back to the cached user so the app
  // renders signed-in immediately. With a cached user we also report
  // isLoading=false so guards don't spin, the background check corrects to
  // signed-out only if the session is genuinely gone.
  const user = session.isPending ? cachedUserSnapshot : resolvedUser;
  return {
    user,
    isLoading: session.isPending && cachedUserSnapshot === null,
  };
}

// Force the reactive useSession() store to re-check the server. Toggling the
// session signal makes better-auth's internal /get-session query refetch:
// a genuine expiry resolves to null (useSession → logged-out, guards show the
// login), while a transient 401 restores the still-valid session, so we never
// log a user out on a momentary blip. Used on app-foreground and on any 401.
export function revalidateSession() {
  authClient.$store.notify("$sessionSignal");
}

export function useAuthActions() {
  const qc = useQueryClient();

  function invalidateSession() {
    // Re-fetch the session after sign-in/up/out so every consumer sees fresh
    // user data without a full reload.
    qc.invalidateQueries({ queryKey: ["session"] });
  }

  return {
    async signUp(email: string, password: string, name: string) {
      const { data, error } = await authClient.signUp.email({
        email,
        password,
        name,
      });
      if (error) throw new Error(error.message ?? "sign-up failed");
      invalidateSession();
      return data.user as AuthUser;
    },
    async signIn(email: string, password: string) {
      const { data, error } = await authClient.signIn.email({
        email,
        password,
      });
      if (error) throw new Error(error.message ?? "sign-in failed");
      invalidateSession();
      return data.user as AuthUser;
    },
    async signOut() {
      await authClient.signOut();
      // Drop the optimistic cache immediately so a cold launch right after
      // sign-out doesn't briefly render the old user.
      cachedUserSnapshot = null;
      writeCachedUser(null);
      invalidateSession();
    },
  };
}
